import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, lastValueFrom, of } from 'rxjs';
import { catchError, map, publishReplay, refCount, tap } from 'rxjs/operators';

import { LoggerService } from '../services/logger.service';
import { ConfigService } from '../services/config.service';
import { ListResponse, Result } from '@shared/models';
import * as Entities from '@shared/entities';
import { User } from '@shared/entities';

@Injectable({
  providedIn: 'root',
})
export class UsersApi {
  private headers = { headers: { 'Content-Type': 'application/json' } };
  private roles: Observable<Entities.Role[]> | null = null;
  private serviceUri = '';

  constructor(
    private http: HttpClient,
    private log: LoggerService,
    private config: ConfigService
  ) {
    this.config.Config.subscribe((config) => {
      if (config?.apiUri) {
        this.serviceUri = `${config.apiUri}/users/`;
      }
    });
  }

  async loadUsers(_force: boolean = false): Promise<ListResponse<User>> {
    const result = await lastValueFrom(this.http.get<Result<ListResponse<User>>>(this.serviceUri));
    if (result.successful) {
      return result.data;
    }

    this.log.displayError(result.message || 'Unable to load users', 'UsersApi->loadUsers');
    return { records: [] } as ListResponse<User>;
  }

  getUserByUserId(id: number): Observable<Entities.User> {
    return this.http.get<Result<Entities.User>>(this.serviceUri + id).pipe(
      map((response) => response.data),
      tap((data) => console.debug('getUserByUserId result:', data)),
      catchError(this.handleError<Entities.User>('getUserByUserId'))
    );
  }

  // Get Roles
  getRoles(): Observable<Entities.Role[]> {
    // cache it once if roles is null
    if (!this.roles) {
      this.roles = this.http.get<Result<ListResponse<Entities.Role>>>(this.serviceUri + 'roles', this.headers).pipe(
        map((response) => response.data.records),
        publishReplay(1), // cache the latest emitted
        refCount(), // keep the observable alive as long as theres a subscriber
        catchError(this.handleError<Entities.Role[]>('roles', []))
      );
    }
    return this.roles;
  }

  async createUser(user: Entities.User): Promise<boolean> {
    try {
      const result = await lastValueFrom(this.http.post<Entities.User>(this.serviceUri, user, this.headers));
      this.log.displaySuccess('User created', 'UsersApi->create');
      return result.userId > 0;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : ((error as { error?: { message?: string } })?.error?.message ?? 'Unknown error');
      this.log.displayError(errorMessage, 'UsersApi->create');
      return false;
    }
  }

  async updateUser(user: Entities.User): Promise<Entities.User | undefined> {
    const userId = user.userId;
    try {
      const result = await lastValueFrom(this.http.put<Result<number>>(this.serviceUri + userId, user, this.headers));
      if (!result.successful) {
        this.log.displayError(result.message || 'Failed to update user', 'UsersApi->update');
        return undefined;
      }

      this.log.displaySuccess('User updated', 'UsersApi->update');
      try {
        const updatedUser = await lastValueFrom(this.getUserByUserId(userId));
        return updatedUser ?? user;
      } catch (error) {
        this.log.logError(error);
        return user;
      }
    } catch (error) {
      this.handleError<Entities.User>('putUser')(error);
      return undefined;
    }
  }

  private handleError<T>(_operation = 'operation', result?: T): (error: unknown) => Observable<T> {
    return (error: unknown): Observable<T> => {
      this.log.logError(error);
      console.error(error);
      return of(result as T);
    };
  }
}
